import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { track, getFilterFromRules } from '@utils';
import { METRIC_SWITCH_MAP } from '@utils/metrics';
import { getUnderperformingAssets } from '@api/autoInsights';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { DASHBOARD_TYPE, INTERNAL_UNKNOWN } from '@utils/constants';
import { AUTO_INSIGHTS_BROKENASSET_CLICK } from '@src/utils/sensor/track';
import { createFilterRule } from '@utils/filter';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { SettingTypes } from '../Settings';
import { metricShortMap, formatInterval } from './formatter';
import { drawerEvent } from '../AlertInsights/hooks';

function useFetchSummary({ to, limit = 100 }) {
  return useQuery(
    [SettingTypes.brokenAssests, to, limit],
    () =>
      getUnderperformingAssets({
        params: {
          to,
          limit,
        },
      }),
    {
      // default cacheTime is 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

function useOpenSummary({ closeMore }) {
  const dispatch = useDispatch();
  const dimensionsList = useSelector(allDimensionsListSelector);
  const rules = [];

  return (values) => {
    const metric = values.rootcause[0];

    const interval = {
      from: formatInterval(values.interval.start),
      to: formatInterval(values.interval.end),
      shortcut: 'custom',
    };

    Object.entries(values.dimension).forEach(([key, value]) => {
      const dimensionObj = dimensionsList.find((d) => d.groupBy === key);

      // Unknown and NULL will not be added to filter list
      if (value === INTERNAL_UNKNOWN || value === 'NULL') {
        return;
      }
      if (key === 'OBJECT_NAME' && dimensionObj) {
        const assetRule = createFilterRule(dimensionsList, dimensionObj.name, value, value);
        rules.push(assetRule);
      }
      if (key === 'channel' && dimensionObj) {
        const channelRule = createFilterRule(dimensionsList, dimensionObj.name, value, value);
        rules.push(channelRule);
      }
      // groupBy for c3.video.isLive is changed to LIVE_VIDEO in if-server
      const contentDimensionObj = dimensionsList.find((d) => d.groupBy === 'LIVE_VIDEO');
      if (key === 'c3.video.isLive' && contentDimensionObj) {
        const contentRule = createFilterRule(dimensionsList, contentDimensionObj.name, 'VoD', 'F');
        rules.push(contentRule);
      }
    });
    const filter = getFilterFromRules(rules, true);

    const dashboard = {
      filter,
      metric: METRIC_SWITCH_MAP[metric] || metric,
      interval,
    };

    dispatch(
      createTempDashboard(
        DASHBOARD_TYPE.BROKEN_ASSETS_DRILLDOWN,
        dashboard,
        `${metricShortMap[metric]} - BrokenAssets`,
      ),
    );

    document.dispatchEvent(drawerEvent);
    closeMore();
    track({ event: AUTO_INSIGHTS_BROKENASSET_CLICK });
  };
}

export { useFetchSummary, useOpenSummary };
